import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  LocateFixed,
  MapPin,
  Search,
  UserPlus,
  X,
} from 'lucide-react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
  addressMarkerService,
  type AddressMarker,
} from '../../services/addressMarkerService';
import CrmEmbeddedCreateOpportunityModal from '../admin-dashboard/CrmEmbeddedCreateOpportunityModal';
import LeadCreateChoiceModal from '../crm/LeadCreateChoiceModal';
import BusinessCardScanModal from '../crm/BusinessCardScanModal';
import { useKeyboardBottomInset } from '../../hooks/useKeyboardBottomInset';

/** Stable reference for useJsApiLoader — inline array retriggers LoadScript every render. */
const googleMapsLibraries: ('places')[] = ['places'];

type PinStatus = Extract<
  AddressMarker['marker_type'],
  'interested' | 'comeback-later' | 'estimate-sent' | 'booked' | 'not-home' | 'not-interested'
>;

const PIN_STATUSES: PinStatus[] = [
  'interested',
  'comeback-later',
  'estimate-sent',
  'booked',
  'not-home',
  'not-interested',
];

/** Statuses worth pursuing — these offer "Drop Pin & Create Lead". */
const LEAD_STATUSES: PinStatus[] = ['interested', 'comeback-later', 'estimate-sent', 'booked'];

/** Which lead-creation UI is showing instead of the pin screen. */
type LeadFlow = 'none' | 'choice' | 'create' | 'scan';

interface SalesPinDropModalProps {
  isOpen: boolean;
  businessId: string;
  onClose: () => void;
  /**
   * Link the dropped pin to an already-existing lead instead of offering the
   * create-lead flow (used from the CRM opportunity create menu).
   */
  existingLead?: {
    opportunityId: number;
    customerId?: string | null;
  };
}

const DEFAULT_CENTER = { lat: 49.2827, lng: -123.1207 }; // Vancouver fallback

/**
 * Current device position. On the native iOS/Android app this uses the
 * Capacitor Geolocation plugin so the already-granted native location
 * permission applies (the webview's navigator.geolocation would re-prompt).
 * Falls back to the browser API on web.
 */
async function getDevicePosition(): Promise<{ lat: number; lng: number }> {
  const { Capacitor } = await import('@capacitor/core');
  if (Capacitor.isNativePlatform()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude };
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('Geolocation is not available');
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      reject,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 },
    );
  });
}

/**
 * Door-to-door sales pin capture: opens on the rep's current GPS location with a
 * fixed center crosshair pin (pan the map to fine-tune), reverse geocodes the
 * address, and records a knock outcome. Positive outcomes offer
 * "Drop Pin & Create Lead", which hands off to the standard create-lead flow
 * (respecting the "AI Create from Business Card" setting) and links the new
 * opportunity + customer back to the pin.
 */
export default function SalesPinDropModal({
  isOpen,
  businessId,
  onClose,
  existingLead,
}: SalesPinDropModalProps) {
  const { user } = useAuth();

  // The native keyboard doesn't resize the webview (Keyboard.resize = 'none'),
  // so we pad the screen with --keyboard-inset — otherwise the keyboard covers
  // the notes field at the bottom with no scroll room.
  useKeyboardBottomInset();

  /**
   * iOS doesn't auto-scroll our inner scroll container to reveal the focused
   * field (the webview never resizes), so do it ourselves once the keyboard
   * animation and inset padding have settled.
   */
  const handleFieldFocus = (e: FocusEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLTextAreaElement) &&
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }
    window.setTimeout(() => {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 300);
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: googleMapsLibraries,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const geocodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [isLocating, setIsLocating] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [pinLatLng, setPinLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [address, setAddress] = useState('');
  const [isGeocoding, setIsGeocoding] = useState(false);

  const [status, setStatus] = useState<PinStatus | null>(null);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Google Business selected via search — shown on screen and saved into notes. */
  const [placeInfo, setPlaceInfo] = useState<{
    name: string;
    phone?: string;
    website?: string;
    location: { lat: number; lng: number };
  } | null>(null);

  // Lead handoff state
  const [aiCardScanEnabled, setAiCardScanEnabled] = useState(false);
  const [leadFlow, setLeadFlow] = useState<LeadFlow>('none');
  /** Newest opportunity id before the create-lead drawer opened (detects what it created). */
  const baselineOpportunityIdRef = useRef<number>(0);
  /** Guards against the scan modal's onClose resetting state while we finalize. */
  const finalizingRef = useRef(false);

  const isLeadStatus = !!status && LEAD_STATUSES.includes(status);

  // Reset everything each time the modal opens, then locate the rep.
  useEffect(() => {
    if (!isOpen) return;
    setStatus(null);
    setNotes('');
    setError(null);
    setAddress('');
    setPlaceInfo(null);
    setPinLatLng(null);
    setCenter(null);
    setLocationDenied(false);
    setLeadFlow('none');
    finalizingRef.current = false;

    setIsLocating(true);
    getDevicePosition()
      .then((loc) => {
        setCenter(loc);
        setPinLatLng(loc);
      })
      .catch((err) => {
        console.warn('Sales pin geolocation failed:', err);
        setLocationDenied(true);
        setCenter(DEFAULT_CENTER);
      })
      .finally(() => setIsLocating(false));
  }, [isOpen]);

  // Respect the CRM "AI Create from Business Card" setting for the lead path.
  // Irrelevant when linking to an existing lead — no lead gets created.
  useEffect(() => {
    if (!isOpen || !businessId || !supabase || existingLead) return;
    supabase
      .rpc('get_app_setting', {
        p_business_id: businessId,
        p_app_slug: 'crm',
        p_setting_key: 'ai_create_from_business_card',
        p_default_value: false,
      })
      .then(({ data }) => setAiCardScanEnabled(!!data))
      .then(undefined, (err: unknown) =>
        console.error('Error loading business card setting:', err),
      );
  }, [isOpen, businessId, existingLead]);

  const reverseGeocode = useCallback((latLng: { lat: number; lng: number }) => {
    if (!isLoaded || typeof google === 'undefined') return;
    setIsGeocoding(true);
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, geocodeStatus) => {
      setIsGeocoding(false);
      if (geocodeStatus === 'OK' && results?.length) {
        setAddress(results[0].formatted_address || '');
      }
    });
  }, [isLoaded]);

  // Reverse geocode whenever the pin position settles (debounced). While the
  // pin sits on a selected Google Business, keep that listing's own address
  // instead of overwriting it with a reverse-geocoded one.
  useEffect(() => {
    if (!pinLatLng) return;
    if (
      placeInfo &&
      Math.abs(placeInfo.location.lat - pinLatLng.lat) <= 0.0006 &&
      Math.abs(placeInfo.location.lng - pinLatLng.lng) <= 0.0006
    ) {
      return;
    }
    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    geocodeTimerRef.current = setTimeout(() => reverseGeocode(pinLatLng), 500);
    return () => {
      if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    };
  }, [pinLatLng, placeInfo, reverseGeocode]);

  // The crosshair pin is fixed at the map center — track it as the map pans.
  const handleMapIdle = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    if (!c) return;
    const latLng = { lat: c.lat(), lng: c.lng() };
    setPinLatLng(latLng);
    // Panning well away from a selected Google Business means the pin no longer
    // represents that business — drop its info (~60m in degrees).
    setPlaceInfo((prev) => {
      if (!prev) return prev;
      const drifted =
        Math.abs(prev.location.lat - latLng.lat) > 0.0006 ||
        Math.abs(prev.location.lng - latLng.lng) > 0.0006;
      return drifted ? null : prev;
    });
  }, []);

  const recenterOnMe = useCallback(() => {
    setIsLocating(true);
    getDevicePosition()
      .then((loc) => {
        mapRef.current?.panTo(loc);
        if ((mapRef.current?.getZoom() || 0) < 18) mapRef.current?.setZoom(19);
        setPinLatLng(loc);
        setLocationDenied(false);
      })
      .catch(() => {
        setLocationDenied(true);
        toast.error('Could not get your location. Check location permissions.');
      })
      .finally(() => setIsLocating(false));
  }, []);

  // Search (Places Autocomplete) — matches both street addresses and Google
  // Business names, so a rep can type "Joe's Plumbing" and land on the listing
  // with its address, phone and website pulled in automatically.
  useEffect(() => {
    if (!isOpen || !isLoaded || leadFlow !== 'none' || !searchInputRef.current) return;
    const ac = new google.maps.places.Autocomplete(searchInputRef.current, {
      // No `types` restriction: default predictions mix addresses + businesses.
      fields: [
        'geometry',
        'formatted_address',
        'name',
        'formatted_phone_number',
        'website',
        'types',
      ],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const loc = place.geometry?.location;
      if (!loc) return;
      const latLng = { lat: loc.lat(), lng: loc.lng() };
      mapRef.current?.panTo(latLng);
      if ((mapRef.current?.getZoom() || 0) < 18) mapRef.current?.setZoom(19);
      setPinLatLng(latLng);
      if (place.formatted_address) setAddress(place.formatted_address);

      // A business listing (not a plain address) carries a name we can use.
      const isBusiness =
        !!place.name &&
        !(place.types || []).some((t) =>
          ['street_address', 'premise', 'subpremise', 'route', 'postal_code'].includes(t),
        );
      setPlaceInfo(
        isBusiness
          ? {
              name: place.name!,
              phone: place.formatted_phone_number || undefined,
              website: place.website || undefined,
              location: latLng,
            }
          : null,
      );
      if (searchInputRef.current) searchInputRef.current.value = '';
    });
    autocompleteRef.current = ac;
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isOpen, isLoaded, leadFlow]);

  const statusColor = useMemo(
    () => (status ? addressMarkerService.getMarkerColor(status) : '#2563EB'),
    [status],
  );

  /** Create the pin, optionally linked to a customer + opportunity, and close. */
  const finalizePin = async (link?: { customerId?: string | null; opportunityId?: number | null }) => {
    if (!pinLatLng || !status || !user?.id) return;
    setIsSaving(true);
    try {
      // createCustomer auto-drops a generic 'customer' pin when the new customer
      // has coordinates — remove it so the knock outcome pin is the only one here.
      // Existing leads keep their long-standing customer marker untouched.
      if (link?.customerId && !existingLead && supabase) {
        await supabase
          .from('address_markers')
          .delete()
          .eq('customer_id', link.customerId)
          .eq('marker_type', 'customer');
      }

      await addressMarkerService.createMarker({
        business_id: businessId,
        latitude: pinLatLng.lat,
        longitude: pinLatLng.lng,
        marker_type: status,
        address: address.trim() || undefined,
        notes: notes.trim() || undefined,
        customer_id: link?.customerId ?? undefined,
        opportunity_id: link?.opportunityId ?? null,
        google_place_name: placeInfo?.name ?? null,
        google_place_phone: placeInfo?.phone ?? null,
        google_place_website: placeInfo?.website ?? null,
        created_by: user.id,
      });

      window.dispatchEvent(new CustomEvent('salesPinCreated'));
      toast.success(
        link?.opportunityId
          ? existingLead
            ? 'Pin dropped and linked to the lead'
            : 'Pin dropped and linked to the new lead'
          : 'Sales pin dropped',
      );
      onClose();
    } catch (err) {
      console.error('Failed to save sales pin:', err);
      const message = err instanceof Error ? err.message : 'Failed to save the pin.';
      setError(message);
      setLeadFlow('none');
      finalizingRef.current = false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDropPin = async () => {
    if (!pinLatLng || !status) return;
    if (!user?.id) {
      setError('You must be signed in to drop a pin.');
      return;
    }
    setError(null);
    await finalizePin(
      existingLead
        ? {
            customerId: existingLead.customerId ?? null,
            opportunityId: existingLead.opportunityId,
          }
        : undefined,
    );
  };

  /** "Drop Pin & Create Lead" — hand off to the standard create-lead flow. */
  const startLeadFlow = async () => {
    if (!pinLatLng || !status) return;
    if (!user?.id) {
      setError('You must be signed in to drop a pin.');
      return;
    }
    setError(null);
    // Remount the map at the adjusted position when the rep comes back.
    setCenter(pinLatLng);

    // Snapshot the newest opportunity id so we can detect what the drawer creates.
    baselineOpportunityIdRef.current = 0;
    try {
      if (supabase) {
        const { data } = await supabase
          .from('pipeline_opportunities')
          .select('id')
          .eq('business_id', businessId)
          .order('id', { ascending: false })
          .limit(1);
        baselineOpportunityIdRef.current = Number(data?.[0]?.id ?? 0);
      }
    } catch (err) {
      console.error('Error snapshotting opportunities:', err);
    }

    setLeadFlow(aiCardScanEnabled ? 'choice' : 'create');
  };

  /** Standard create-lead drawer closed — link the pin if a lead was created. */
  const handleCreateDrawerClosed = async () => {
    try {
      if (supabase) {
        const { data } = await supabase
          .from('pipeline_opportunities')
          .select('id, customer_id')
          .eq('business_id', businessId)
          .gt('id', baselineOpportunityIdRef.current)
          .order('id', { ascending: false })
          .limit(1);
        const created = data?.[0];
        if (created) {
          await finalizePin({
            customerId: created.customer_id as string | null,
            opportunityId: Number(created.id),
          });
          return;
        }
      }
    } catch (err) {
      console.error('Error checking for created lead:', err);
    }
    // Drawer was cancelled — back to the pin screen.
    setLeadFlow('none');
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-colors';
  const labelClass =
    'block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1';

  const pinScreen = (
    <div
      className="fixed inset-0 bg-white flex flex-col"
      style={{
        zIndex: 100060,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        paddingTop: 'env(safe-area-inset-top)',
        // Keeps the footer + focused field above the iOS keyboard (the
        // webview itself never resizes — Keyboard.resize is 'none').
        paddingBottom: 'var(--keyboard-inset, 0px)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-white">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <MapPin className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              Sales Pin
            </h2>
            <p className="text-xs text-gray-500">
              {existingLead
                ? 'Pin will be linked to this lead'
                : 'Pan the map to fine-tune the pin'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
          aria-label="Close"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Map with fixed center crosshair pin */}
      <div className="relative flex-shrink-0" style={{ height: '38vh', minHeight: 220 }}>
        {isLoaded && center ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={19}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            onIdle={handleMapIdle}
            options={{
              disableDefaultUI: true,
              zoomControl: false,
              clickableIcons: false,
              gestureHandling: 'greedy',
              mapTypeId: 'hybrid',
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isLocating ? 'Finding your location…' : 'Loading map…'}
            </div>
          </div>
        )}

        {/* Center crosshair — status badge sits exactly on the target point */}
        {isLoaded && center && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-[9px] border-[2.5px] border-white"
              style={{
                backgroundColor: statusColor,
                boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
            </span>
          </div>
        )}

        {/* Address search overlay */}
        <div className="absolute top-3 left-3 right-14">
          <div className="flex items-center gap-2 bg-white/95 backdrop-blur rounded-xl shadow-md px-3 py-2">
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search address or business name…"
              className="w-full bg-transparent text-sm text-gray-900 focus:outline-none"
            />
          </div>
        </div>

        {/* Locate me */}
        <button
          type="button"
          onClick={recenterOnMe}
          disabled={isLocating}
          className="absolute bottom-3 right-3 h-11 w-11 flex items-center justify-center rounded-full bg-white shadow-lg active:scale-95 transition-transform disabled:opacity-60"
          aria-label="Use my location"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {isLocating ? (
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
          ) : (
            <LocateFixed className="h-5 w-5 text-blue-600" />
          )}
        </button>
      </div>

      {/* Details */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        onFocusCapture={handleFieldFocus}
      >
        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
            {error}
          </div>
        )}

        {locationDenied && (
          <div className="px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800">
            Location unavailable — search an address above or pan the map to place the pin.
          </div>
        )}

        {/* Resolved address */}
        <div className="flex items-start gap-2.5 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
          <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-800 leading-snug">
            {isGeocoding ? (
              <span className="text-gray-400">Finding address…</span>
            ) : (
              address || <span className="text-gray-400">Move the map to set a location</span>
            )}
          </div>
        </div>

        {/* Selected Google Business */}
        {placeInfo && (
          <div className="px-4 py-3 bg-blue-50/70 border border-blue-100 rounded-xl">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-500 mb-0.5">
                  Google Business
                </div>
                <div className="text-sm font-semibold text-gray-900 truncate">{placeInfo.name}</div>
                {placeInfo.phone && (
                  <div className="text-xs text-gray-600 mt-0.5">{placeInfo.phone}</div>
                )}
                {placeInfo.website && (
                  <div className="text-xs text-blue-700 mt-0.5 truncate">{placeInfo.website}</div>
                )}
                <div className="text-[11px] text-gray-500 mt-1">Saved with the pin</div>
              </div>
              <button
                type="button"
                onClick={() => setPlaceInfo(null)}
                className="h-7 w-7 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-blue-100 active:scale-95 transition-all"
                aria-label="Remove business info"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-4 w-4 text-blue-500" />
              </button>
            </div>
          </div>
        )}

        {/* Knock outcome */}
        <div>
          <label className={labelClass}>Knock Outcome</label>
          <div className="grid grid-cols-2 gap-2">
            {PIN_STATUSES.map((s) => {
              const selected = status === s;
              const color = addressMarkerService.getMarkerColor(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all active:scale-[0.98] ${
                    selected ? 'text-white border-transparent' : 'bg-white text-gray-700 border-gray-200'
                  }`}
                  style={{
                    backgroundColor: selected ? color : undefined,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selected ? '#ffffff' : color }}
                  />
                  {addressMarkerService.getMarkerTypeLabel(s)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className={labelClass}>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='e.g. "Call back after 6pm", "Owner asked for a quote"'
            className={`${inputClass} resize-none`}
            rows={2}
          />
        </div>
      </div>

      {/* Footer — Drop Pin, plus Drop Pin & Create Lead for positive outcomes */}
      <div
        className="px-4 py-3 border-t border-gray-100 flex-shrink-0 bg-white space-y-2"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      >
        {isLeadStatus && !existingLead && (
          <button
            onClick={() => void startLeadFlow()}
            disabled={!status || !pinLatLng || isSaving}
            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-all active:scale-[0.98] disabled:opacity-50"
            style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)' }}
          >
            <UserPlus className="h-4 w-4" />
            Drop Pin &amp; Create Lead
          </button>
        )}
        <button
          onClick={() => void handleDropPin()}
          disabled={!status || !pinLatLng || isSaving}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] disabled:opacity-50 ${
            isLeadStatus && !existingLead
              ? 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
          style={
            isLeadStatus && !existingLead
              ? undefined
              : { boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)' }
          }
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <MapPin className="h-4 w-4" />
              Drop Pin
            </>
          )}
        </button>
      </div>
    </div>
  );

  return createPortal(
    <>
      {leadFlow === 'none' && pinScreen}

      {leadFlow === 'choice' && (
        <LeadCreateChoiceModal
          isOpen
          onClose={() => setLeadFlow('none')}
          onCreate={() => setLeadFlow('create')}
          onCreateFromPhoto={() => setLeadFlow('scan')}
        />
      )}

      {leadFlow === 'create' && (
        <CrmEmbeddedCreateOpportunityModal
          onClose={() => void handleCreateDrawerClosed()}
        />
      )}

      {leadFlow === 'scan' && (
        <BusinessCardScanModal
          isOpen
          businessId={businessId}
          onClose={() => {
            if (!finalizingRef.current) setLeadFlow('none');
          }}
          onCreated={(opportunityId, customerId) => {
            finalizingRef.current = true;
            void finalizePin({ customerId, opportunityId });
          }}
        />
      )}
    </>,
    document.body,
  );
}
